import { PocTableCell } from 'commons/components/components-wrapper';
import { OwcCheckboxCustomEvent, OwcTableRowCustomEvent } from '@one/web-components/dist/types/components';
import { useHover } from 'commons/components/table/hooks';
import { DEFAULT_HOVER_DELAY } from 'core/constants/constants';
import React from 'react';
import { TooltipedCustomFieldCell } from './components/TooltipedCustomFieldCell.component';
import { CustomTableConfig, defaultCellOptions, FieldConfig } from './custom-table.type';
import { OwcTableRowOptionsStyled, OwcTableRowStyled } from './custom-table.style';

export interface TableRowType<T extends { id: string }> {
    data: T;
    config: CustomTableConfig<T>;
    isSelected?: boolean;
    someRowsAreSelected?: boolean;
    fieldsToShow: FieldConfig<T>[];
    clickedRow?: string;
    rowOptions?: (data: T) => React.ReactElement[];
    onClickRow?: (id: string) => void;
    onSelectRow?: (id: string, event: OwcTableRowCustomEvent<boolean>) => void;
    interactive?: boolean;
}

export const TableRow = <T extends { id: string }>({
    data,
    config,
    isSelected,
    someRowsAreSelected,
    fieldsToShow,
    rowOptions,
    clickedRow,
    onClickRow,
    onSelectRow,
    interactive = true,
}: TableRowType<T>) => {
    const { ref, isHovered } = useHover<HTMLOwcTableRowElement>({ delayMs: DEFAULT_HOVER_DELAY });

    const handleOnClickRow = (event: React.MouseEvent<HTMLOwcTableRowElement, MouseEvent>, id: string) => {
        event.preventDefault();
        // Ignore clicks bubbling from the selection checkbox so selecting a row doesn't also
        // open the side sheet. OWC-OVERRIDE (DOM read, not a style override):
        // .owc-table-row__leading-cell is owc's internal class for the selection cell — in
        // v7.6.0 owc-table-row emits no event or attribute that distinguishes it. Revisit on the next @one upgrade.
        if ((event.target as HTMLElement).closest('owc-checkbox, .owc-table-row__leading-cell')) {
            return;
        }
        onClickRow?.(id);
    };

    const getDefaultDataValue = (obj: T, field: string): string =>
        String(field in obj ? (obj[field as keyof T] ?? '') : field);

    const getDefaultRowCheckboxProps = (id: string) => ({
        checked: isSelected,
        onValueChange: (ev: OwcCheckboxCustomEvent<boolean>) => {
            ev.preventDefault();
            onSelectRow?.(id, ev);
        },
    });

    const rowClassNames = (rowData: T, rowClassName: string | undefined): string => {
        let className = clickedRow === rowData.id ? 'drawer-opened' : '';
        className += rowOptions ? ' with-row-options' : '';
        className += rowClassName ? ` ${rowClassName}` : '';

        return className;
    };

    const groupOptionsElements = rowOptions ? rowOptions(data) : [];
    const showGroupOptions = Boolean(isHovered && !someRowsAreSelected && groupOptionsElements.length > 0);

    return (
        <OwcTableRowStyled
            ref={ref}
            id={`table_body_row_${data.id}`}
            data-testid={`table-row-${data.id}`}
            key={`table_body_row_${data.id}`}
            selectable={interactive}
            locked={interactive}
            className={rowClassNames(data, config.rowStyles?.(data))}
            onClick={interactive ? (event) => handleOnClickRow(event, data.id) : undefined}
            selected={isSelected}
            checkboxProps={interactive ? getDefaultRowCheckboxProps(data.id) : undefined}
            $hovered={isHovered}
            $interactive={interactive}
        >
            {fieldsToShow.filter(Boolean).map((field) => (
                <PocTableCell
                    key={`table_body_row_${data.id}_${field.name}`}
                    align={field.rowOptions?.cellOptions?.align ?? defaultCellOptions.align}
                    valign={field.rowOptions?.cellOptions?.valign ?? defaultCellOptions.valign}
                    className={field.rowOptions?.cellOptions?.className}
                    style={field.rowOptions?.cellOptions?.style}
                >
                    {field.rowOptions?.cellOptions?.format?.(data) ?? (
                        <TooltipedCustomFieldCell value={getDefaultDataValue(data, field.name)} />
                    )}
                </PocTableCell>
            ))}

            {showGroupOptions && (
                <OwcTableRowOptionsStyled className="owc-table-row-options" data-testid="row-options">
                    {groupOptionsElements.map((comp, index) => (
                        <div
                            data-testid="row-options"
                            className="owc-table-row-option"
                            key={`table_body_row_${data.id}_option_${index}`}
                        >
                            {comp}
                        </div>
                    ))}
                </OwcTableRowOptionsStyled>
            )}
        </OwcTableRowStyled>
    );
};
